import { useCallback, useEffect, useState } from 'react';
import {
  reconciliation as loadReport,
  runReconciliation,
  type ReconciliationReport,
} from './api';
import type { LoyaltyTranslate } from './i18n';
import { Table, TableSkeleton } from '../../shell/kit';

export interface ReconciliationTabProps {
  readonly t: LoyaltyTranslate;
  readonly onError: (message: string) => void;
}

export function ReconciliationTab({ t, onError }: ReconciliationTabProps) {
  const [report, setReport] = useState<ReconciliationReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      setReport(await loadReport());
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const findings = report?.findings ?? [];

  return (
    <section className="space-y-4">
      <header className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-ink">{t('recon.title')}</h3>
        <button
          type="button"
          disabled={busy}
          className="inline-flex h-8 items-center justify-center rounded-control bg-brand-600 px-3 text-sm font-medium text-ink-invert shadow-card transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-muted disabled:shadow-none"
          onClick={() => {
            setBusy(true);
            runReconciliation()
              .then((r) => setReport(r))
              .catch((err: Error) => onError(err.message))
              .finally(() => setBusy(false));
          }}
        >
          {t('recon.run')}
        </button>
      </header>

      {!report ? (
        <p className="text-ink-muted">{t('recon.never')}</p>
      ) : (
        <>
          <dl className="grid grid-cols-2 gap-3 rounded border border-line bg-surface p-4 sm:grid-cols-3">
            <div>
              <dt className="text-xs text-ink-muted">{t('recon.checked')}</dt>
              <dd className="text-lg font-semibold">{report.run.accountsChecked}</dd>
            </div>
            <div>
              <dt className="text-xs text-ink-muted">{t('recon.findings')}</dt>
              <dd
                className={`text-lg font-semibold ${
                  report.run.findingsCount === 0 ? 'text-ok' : 'text-danger'
                }`}
              >
                {report.run.findingsCount}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-ink-muted">{t('account.date')}</dt>
              <dd className="text-sm">{new Date(report.run.startedAt).toLocaleString()}</dd>
            </div>
          </dl>

          {loading ? (
            <TableSkeleton rows={4} columns={4} />
          ) : findings.length === 0 ? (
            <p className="text-ok">{t('recon.empty')}</p>
          ) : (
            <>
              <p className="text-sm font-medium text-danger">{t('recon.incident')}</p>
              <div className="overflow-x-auto rounded border border-line bg-surface">
                <Table className="text-left">
                  <thead className="bg-sunken text-xs uppercase text-ink-muted">
                    <tr>
                      <th className="px-3 py-2">{t('recon.customer')}</th>
                      <th className="px-3 py-2">{t('recon.materialized')}</th>
                      <th className="px-3 py-2">{t('recon.ledger')}</th>
                      <th className="px-3 py-2">{t('recon.delta')}</th>
                      <th className="px-3 py-2">{t('recon.repaired')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {findings.map((finding) => (
                      <tr key={finding.id} className="border-t border-line">
                        <td className="px-3 py-2 font-mono text-xs">{finding.customerId}</td>
                        <td className="px-3 py-2">{finding.materializedMinor}</td>
                        <td className="px-3 py-2">{finding.ledgerMinor}</td>
                        <td className="px-3 py-2 font-medium text-danger">
                          {finding.deltaMinor}
                        </td>
                        <td className="px-3 py-2">
                          {finding.repaired ? t('common.yes') : t('common.no')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
}
